import styles from './TextInput.module.scss';
import {
  forwardRef,
  type ReactNode,
  Ref,
  useRef,
  HTMLInputTypeAttribute,
  ChangeEvent,
  useContext,
  useEffect,
} from 'react';
import { useMergeRefs } from '@libs/react/ref';
import { InputShared } from './InputShared';
import { FormContext } from '../form/FormContext';
import { FormFieldContext } from '../form/FormFieldContext';

type Message = {
  content: string;
  type: 'error' | 'info';
};

type Props = {
  addon?: ReactNode;
  bottom?: ReactNode;
  defaultValue?: string | number;
  disabled?: boolean;
  grow?: boolean | number;
  id?: string;
  label?: string;
  lines?: number;
  max?: number;
  maxLength?: number;
  message?: Message;
  min?: number;
  name?: string;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onFocus?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onKeyDown?: (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onValue?: (value: string) => void;
  prefix?: ReactNode;
  required?: boolean;
  type?: HTMLInputTypeAttribute;
  valid?: boolean;
  value?: string;
  width?: string;
};

export const TextInput = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  Props
>(
  (
    {
      addon,
      bottom,
      defaultValue,
      disabled,
      grow,
      label,
      lines = 1,
      max,
      maxLength,
      min,
      onBlur,
      onFocus,
      onKeyDown,
      prefix,
      required = false,
      type = 'text',
      width,
      onValue,
      valid,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const mergedRef = useMergeRefs<HTMLInputElement | HTMLTextAreaElement>(
      ref,
      inputRef,
    );
    const formContext = useContext(FormContext);
    const formFieldContext = useContext(FormFieldContext);
    const name = props.name ?? formFieldContext.name;

    useEffect(() => {
      if (name && inputRef.current) {
        const prev = formContext.inputs.get(name) ?? [];
        formContext.inputs.set(name, [...prev, inputRef.current]);
      }
      return () => {
        formContext.inputs.delete(name);
      };
    }, [formContext, name]);

    const onClick = () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };

    function onChangeInput(event: ChangeEvent<HTMLInputElement>) {
      const value = event.currentTarget.value;
      props.onChange?.(event);
      onValue?.(value);
    }

    function onChangeTextarea(event: ChangeEvent<HTMLTextAreaElement>) {
      const value = event.currentTarget.value;
      props.onChange?.(event);
      onValue?.(value);
    }

    const inputClassName = [styles['text-input-element']]
      .filter(Boolean)
      .join(' ');

    return (
      <InputShared
        bottom={bottom}
        end={addon}
        id={props.id}
        label={label}
        name={name}
        onClick={onClick}
        valid={valid}
      >
        {({ id }) => {
          return lines === 1 ? (
            <div className={styles['text-input-field']}>
              {prefix && (
                <span className={styles['text-input-prefix']}>{prefix}</span>
              )}
              <input
                ref={mergedRef as Ref<HTMLInputElement>}
                className={inputClassName}
                defaultValue={defaultValue}
                disabled={disabled}
                id={id}
                max={max}
                maxLength={maxLength}
                min={min}
                name={name}
                onBlur={onBlur}
                onChange={onChangeInput}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
                required={required}
                type={type}
              />
            </div>
          ) : (
            <textarea
              ref={mergedRef as Ref<HTMLTextAreaElement>}
              className={inputClassName}
              defaultValue={defaultValue}
              disabled={disabled}
              maxLength={maxLength}
              name={name}
              onBlur={onBlur}
              onChange={onChangeTextarea}
              onFocus={onFocus}
              onKeyDown={onKeyDown}
              required={required}
            />
          );
        }}
      </InputShared>
    );
  },
);
